import { getThreeColGoodsListData } from './getThreeColGoodsListData';
import type { ThreeColGoodsListProps, ThreeColGoodsListItem } from './types';
import { Flex, GoodsCard, List, useScreenSize } from '@banhala/ui-kit';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { bridge, useNavigator } from '../../../../utils';
import { logOnGoodsPressItem } from './log/logOnGoodsPressItem';
import { logOnPressLike } from './log/logOnPressLike';
import { useLogDisplayGoods } from './log/useLogDisplayGoods';
import { useEffect, useMemo } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';
import { useAddLikeGoods } from '../../../../hooks';

export const ThreeColGoodsList = ({
  entity,
  likeMap,
  setLikeMap,
  screen,
}: ThreeColGoodsListProps) => {
  const goodsData = getThreeColGoodsListData(entity);
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [entity, setLikeMap]);

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { logDisplayGoods } = useLogDisplayGoods(screen);
  const { addLikeGoods } = useAddLikeGoods({
    listParams: entity?.[0]?.logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (entity?.[0]?.logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ item, logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      loggingAnalytics: logging.analytics,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ logging, item, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };
  const itemWidth = useMemo(() => {
    return (screenWidth - 4) / 3;
  }, [screenWidth]);

  {
    /*
     * ui-kit의 GoodsCard3Column를 사용 안한 이유
     * 현재 상품 상세에서 Atomic을 사용 하는 경우,
     * depth는 다음의 형태로 가지고 있습니다
     * List > Proxy > List > GoodsCard3Column
     * 여기서 GridView에 height를 지정 해 주지 않으면 height가 0으로 인식되지 않는 이슈가 있음
     * GridView에서 height를 선언하면 해결 되는것으로 확인하였으나, 그렇게 할 경우 칩이 늘어나는 등 했을때 height를 계산하는 로직의 복잡도가 올라가게 됨
     * 차라리 GridView를 사용하지 않고, 아래의 형태로 사용하는게 좀더 나을것으로 보이는데, 우선적으로 여기만 이렇게 적용을 해두고,
     * 추후에 ui-kit에서 안정성 검증 이후에 수정해보도록 합니다.
     */
  }
  return (
    <List<ThreeColGoodsListItem>
      data={goodsData}
      numColumns={3}
      onViewableItemsChanged={logDisplayGoods}
      columnWrapperStyle={{ gap: 2 }}
      keyExtractor={item =>
        item.type === 'placeholder' ? item.key : String(item.data.sno)
      }
      renderItem={({ item, index }) => {
        if (item.type === 'placeholder') {
          return <Flex flex={1} />;
        }
        const { data } = item;
        return (
          <GoodsCard
            data={data}
            size={'medium'}
            testID={`goods/${data.sno}?LIST_ID=${data.logging.analytics?.LIST_ID}&LIST_INDEX=${index}&DELIVERY_TYPE=${data.deliveryType}`}
            defaultWidth={itemWidth}
            onPress={() => handlePressItem(data)}
            onPressLike={async () => {
              await handlePressLike(data);
            }}
            isLike={likeMap[data.sno]}
          />
        );
      }}
    />
  );
};
